import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import IncidentInvestigationModal from '../components/incidents/IncidentInvestigationModal';
import IncidentModal, {
  type Incident,
  INCIDENT_TYPE_LABELS,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  INCIDENT_STATUS_LABELS,
} from '../components/incidents/IncidentModal';
import { requiresCd513Investigation } from '../lib/cd513-catalog';
import { openIncidentEvidence } from '../lib/incidentEvidence';
import IncidentAvisoMedidasModal from '../components/incidents/IncidentAvisoMedidasModal';
import { COLUMNAS_MEDIDA, type FilaMedida } from '../lib/incidentes/inc-datos';
import { ETIQUETA_AVISO, ITEMS_INCIDENTES_LOTE_E, alertasAviso, estadoAvisoCaso, requiereAviso, resultadosIncidentes, type EstadoAviso } from '../lib/incidentes/inc-estado';
import { ADVERTENCIA_NO_APLICA_CON_CASOS, itemsNoAplicaConCasos } from '../lib/incidentes/inc-checklist';
import type { EstadoGuardadoChecklist } from '../lib/plan-emergencias-compliance-sync';
import { generarResumenAnualPDF } from '../lib/incidentes/inc-pdf';
import { fechaCorta, hoyIso, textoVence } from '../lib/incidentes/inc-plazos';

// ── Types ──────────────────────────────────────────────────────────────────────

interface WorkerOption { id: string; full_name: string; cedula: string | null; }
interface Warehouse { id: string; name: string; }

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  abierto:          '#ef4444',
  en_investigacion: '#f59e0b',
  cerrado:          '#22c55e',
};

function currentYearMonths(): number {
  return new Date().getMonth() + 1; // 1-based
}

function hoursWorked(activeWorkers: number): number {
  return activeWorkers * 8 * 26 * currentYearMonths();
}

// Lote E: color del estado del aviso al IESS en la lista.
const COLOR_AVISO: Record<EstadoAviso, string> = { con_constancia: '#22c55e', fuera_de_termino: '#f59e0b', en_termino: '#38bdf8', vencido: '#ef4444' };

function fmt2(n: number): string {
  return isNaN(n) || !isFinite(n) ? '—' : n.toFixed(2);
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function IncidentsPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const canEdit = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;

  const [incidents, setIncidents]   = useState<Incident[]>([]);
  const [workers, setWorkers]       = useState<WorkerOption[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [activeWorkerCount, setActiveWorkerCount] = useState(0);
  const [loading, setLoading]       = useState(true);

  // Filters
  const [search, setSearch]             = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterYear, setFilterYear]     = useState(String(new Date().getFullYear()));

  // Modal
  const [modalIncident, setModalIncident] = useState<Incident | null | undefined>(undefined);
  const [tipoNuevo, setTipoNuevo] = useState<Incident['incident_type'] | undefined>(undefined);
  const [investigationIncidentId, setInvestigationIncidentId] = useState<string | undefined>(undefined);
  const [avisoIncident, setAvisoIncident] = useState<Incident | null>(null);
  const [generandoResumen, setGenerandoResumen] = useState(false);
  const [estadosChecklist, setEstadosChecklist] = useState<EstadoGuardadoChecklist[]>([]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: inc }, { data: wks }, { data: whs }, { count: activeCount }] = await Promise.all([
      supabase.from('incidents').select('*').eq('company_id', companyId).order('incident_date', { ascending: false }),
      supabase.from('workers').select('id, full_name, cedula').eq('company_id', companyId).eq('status', 'activo').order('full_name'),
      supabase.from('warehouses').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('workers').select('*', { count: 'exact', head: true }).eq('company_id', companyId).eq('status', 'activo'),
    ]);
    setIncidents((inc ?? []) as Incident[]);
    setWorkers((wks ?? []) as WorkerOption[]);
    setWarehouses((whs ?? []) as Warehouse[]);
    setActiveWorkerCount(activeCount ?? 0);
    setLoading(false);
    // Lote E: PO 9, 10, 12 y 13 marcados "No aplica" en el Checklist (para avisar si ya hay casos en el periodo).
    const { data: est } = await supabase.from('checklist_item_estados').select('item_id, estado, auto_source')
      .eq('company_id', companyId).in('item_id', [...ITEMS_INCIDENTES_LOTE_E]);
    setEstadosChecklist((est ?? []) as EstadoGuardadoChecklist[]);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleSaved() { setModalIncident(undefined); loadData(); }

  // Lote E: resumen anual de casos, avisos y medidas (año del filtro).
  async function descargarResumenAnual() {
    setGenerandoResumen(true);
    try {
      const anio = Number(filterYear) || new Date().getFullYear();
      const [{ data: meds, error }, { data: profs }] = await Promise.all([
        supabase.from('incident_actions').select(COLUMNAS_MEDIDA).eq('company_id', companyId),
        supabase.from('profiles').select('id, full_name, email').eq('company_id', companyId),
      ]);
      if (error) throw new Error(error.message);
      const nombre = new Map(((profs ?? []) as { id: string; full_name: string | null; email: string | null }[]).map(p => [p.id, p.full_name || p.email || '']));
      const doc = generarResumenAnualPDF({
        empresa: displayCompany?.name ?? '', anio, hoy: hoyIso(), fecha: new Date(),
        casos: incidents.map(i => ({
          ...i, tipoEtiqueta: INCIDENT_TYPE_LABELS[i.incident_type], trabajador: i.worker_id ? workerMap.get(i.worker_id)?.full_name ?? null : null,
        })),
        medidas: ((meds ?? []) as unknown as FilaMedida[]).map(m => ({
          ...m, responsable: [m.responsible_id ? nombre.get(m.responsible_id) : '', m.responsible_name ?? ''].filter(Boolean).join(' · '),
        })),
      });
      doc.save(`resumen-incidentes-${anio}.pdf`);
    } catch (e) {
      alert('No se pudo generar el resumen anual: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setGenerandoResumen(false);
    }
  }

  // ── Maps ──────────────────────────────────────────────────────────────────────

  const workerMap = new Map(workers.map(w => [w.id, w]));
  const warehouseMap = new Map(warehouses.map(w => [w.id, w.name]));

  // ── Metrics (current year) ────────────────────────────────────────────────────

  const hoy = hoyIso();
  const alertas = alertasAviso(incidents, hoy);
  const revisarNoAplica = itemsNoAplicaConCasos(estadosChecklist, resultadosIncidentes(incidents, [], hoy));
  const incidentMap = new Map(incidents.map(i => [i.id, i]));

  const thisYear = new Date().getFullYear();
  const yearIncidents = incidents.filter(i => new Date(i.incident_date).getFullYear() === thisYear);
  const accidents = yearIncidents.filter(i => i.incident_type === 'accidente_trabajo');
  const nearMisses = yearIncidents.filter(i => i.incident_type === 'incidente');
  const totalDaysLost = yearIncidents.reduce((sum, i) => sum + (i.days_lost ?? 0), 0);

  const hours = hoursWorked(activeWorkerCount);
  const IF = hours > 0 ? (accidents.length * 200000) / hours : NaN;
  const IG = hours > 0 ? (totalDaysLost * 200000) / hours : NaN;

  // ── Filtered ──────────────────────────────────────────────────────────────────

  const filtered = incidents.filter(i => {
    if (filterYear && String(new Date(i.incident_date).getFullYear()) !== filterYear) return false;
    if (filterType && i.incident_type !== filterType) return false;
    if (filterSeverity && i.severity !== filterSeverity) return false;
    if (filterStatus && i.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const workerName = i.worker_id ? (workerMap.get(i.worker_id)?.full_name ?? '') : '';
      if (!workerName.toLowerCase().includes(q) && !i.description.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── Year options ──────────────────────────────────────────────────────────────

  const years = Array.from(new Set(incidents.map(i => new Date(i.incident_date).getFullYear()))).sort((a, b) => b - a);
  if (!years.includes(thisYear)) years.unshift(thisYear);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Accidentes e Incidentes</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {yearIncidents.length} evento{yearIncidents.length !== 1 ? 's' : ''} registrado{yearIncidents.length !== 1 ? 's' : ''} en {thisYear}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={descargarResumenAnual} disabled={generandoResumen} style={secondaryBtn}>
            {generandoResumen ? 'Generando…' : `Resumen ${filterYear} (PDF)`}
          </button>
          {canEdit && (
            <button type="button" onClick={() => { setTipoNuevo('enfermedad_profesional'); setModalIncident(null); }} style={secondaryBtn}>
              + Presunción de enfermedad profesional
            </button>
          )}
          {canEdit && (
            <button onClick={() => { setTipoNuevo(undefined); setModalIncident(null); }} style={primaryBtn}>
              + Registrar
            </button>
          )}
        </div>
      </div>

      {/* Lote E: "No aplica" del Checklist con casos en el periodo */}
      {revisarNoAplica.length > 0 && (
        <div role="alert" style={{ border: '2px solid #dc2626', background: 'rgba(220,38,38,0.15)', color: '#fecaca', borderRadius: 10, padding: '10px 14px', fontSize: '0.84rem', fontWeight: 700, lineHeight: 1.45 }}>
          ⚠ {ADVERTENCIA_NO_APLICA_CON_CASOS}. En el Checklist están marcados "No aplica": {revisarNoAplica.map(i => i.replace('-0', ' ').replace('-', ' ')).join(', ')}.
        </div>
      )}

      {/* Lote E: avisos al IESS pendientes o vencidos */}
      {alertas.length > 0 && (
        <div role="status" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ color: '#f1f5f9', fontSize: '0.86rem', fontWeight: 700 }}>Aviso al IESS (10 días hábiles)</span>
          {alertas.map(a => {
            const inc = incidentMap.get(a.id);
            const quien = inc?.worker_id ? workerMap.get(inc.worker_id)?.full_name : null;
            return (
              <button key={a.id} type="button" onClick={() => inc && setAvisoIncident(inc)}
                style={{ textAlign: 'left', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', color: a.estado === 'vencido' ? '#fca5a5' : '#7dd3fc', fontSize: '0.82rem', lineHeight: 1.4 }}>
                <strong>{a.estado === 'vencido' ? 'Vencido sin aviso' : textoVence(a.fechaLimite, hoy)}</strong>
                {' · '}{a.tipo === 'enfermedad' ? 'Presunción de enfermedad profesional' : 'Accidente'} del {fechaCorta(a.fecha)}
                {quien ? ` · ${quien}` : ''} · límite {fechaCorta(a.fechaLimite)}
              </button>
            );
          })}
          <span style={{ color: '#64748b', fontSize: '0.72rem' }}>Se cuentan de lunes a viernes; no se descuentan feriados: conviene enviar el aviso antes.</span>
        </div>
      )}

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12 }}>
        <MetricCard label={`Total ${thisYear}`} value={yearIncidents.length} color="#a78bfa" bg="rgba(167,139,250,0.1)" />
        <MetricCard label="Accidentes de trabajo" value={accidents.length} color="#ef4444" bg="rgba(239,68,68,0.1)" />
        <MetricCard label="Incidentes / casi-accidentes" value={nearMisses.length} color="#f59e0b" bg="rgba(245,158,11,0.1)" />
        <MetricCard label="Días perdidos acumulados" value={totalDaysLost} color="#fb923c" bg="rgba(249,115,22,0.1)" />
      </div>

      {/* Índices */}
      {activeWorkerCount > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <IndexCard label="Índice de Frecuencia" value={fmt2(IF)} subtitle="Accidentes × 200 000 / horas trabajadas" ref_="IESS / DE 255" />
          <IndexCard label="Índice de Gravedad" value={fmt2(IG)} subtitle="Días perdidos × 200 000 / horas trabajadas" ref_="IESS / DE 255" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...filterInp, flex: '1 1 180px', minWidth: 150 }} placeholder="Buscar por trabajador o descripción…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={filterInp} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        <select style={filterInp} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Tipo: todos</option>
          {(Object.keys(INCIDENT_TYPE_LABELS) as Array<keyof typeof INCIDENT_TYPE_LABELS>).map(t =>
            <option key={t} value={t}>{INCIDENT_TYPE_LABELS[t]}</option>)}
        </select>
        <select style={filterInp} value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
          <option value="">Gravedad: todas</option>
          {(Object.keys(SEVERITY_LABELS) as Array<keyof typeof SEVERITY_LABELS>).map(s =>
            <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>)}
        </select>
        <select style={filterInp} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Estado: todos</option>
          {(Object.keys(INCIDENT_STATUS_LABELS) as Array<keyof typeof INCIDENT_STATUS_LABELS>).map(s =>
            <option key={s} value={s}>{INCIDENT_STATUS_LABELS[s]}</option>)}
        </select>
        {(search || filterType || filterSeverity || filterStatus) && (
          <button onClick={() => { setSearch(''); setFilterType(''); setFilterSeverity(''); setFilterStatus(''); }}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.82rem' }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {incidents.length === 0 ? 'Aún no hay eventos registrados.' : 'Sin resultados para los filtros aplicados.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Fecha', 'Tipo', 'Trabajador afectado', 'Bodega', 'Gravedad', 'CD 513', 'Días perdidos', 'IESS', 'Estado', ''].map((h, i) => (
                    <th key={`${h}_${i}`} style={{ padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inc => {
                  const worker = inc.worker_id ? workerMap.get(inc.worker_id) : null;
                  const sc = SEVERITY_COLORS[inc.severity];
                  return (
                    <tr
                      key={inc.id}
                      onClick={() => canEdit && setModalIncident(inc)}
                      style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', cursor: canEdit ? 'pointer' : 'default', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={td}>
                        <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{new Date(inc.incident_date + 'T00:00:00').toLocaleDateString('es-EC')}</span>
                        {inc.incident_time && <span style={{ color: '#64748b', fontSize: '0.74rem', display: 'block' }}>{inc.incident_time.slice(0, 5)}</span>}
                      </td>
                      <td style={td}>
                        <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>{INCIDENT_TYPE_LABELS[inc.incident_type]}</span>
                      </td>
                      <td style={td}>
                        {worker ? (
                          <div>
                            <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{worker.full_name}</span>
                            {worker.cedula && <span style={{ color: '#64748b', fontSize: '0.74rem', display: 'block' }}>{worker.cedula}</span>}
                          </div>
                        ) : <span style={{ color: '#475569' }}>—</span>}
                      </td>
                      <td style={{ ...td, color: '#94a3b8', fontSize: '0.8rem' }}>
                        {inc.warehouse_id ? (warehouseMap.get(inc.warehouse_id) ?? '—') : '—'}
                      </td>
                      <td style={td}>
                        <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700 }}>
                          {SEVERITY_LABELS[inc.severity]}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {requiresCd513Investigation(inc.severity, inc.days_lost) && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <span title="Requiere Investigación formal CD 513"
                              style={{ color: '#f87171', fontSize: '1rem' }}>⚠️</span>
                            <button
                              onClick={e => { e.stopPropagation(); setInvestigationIncidentId(inc.id); }}
                              style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '0.68rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                            >
                              Investigación CD 513
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, color: inc.days_lost > 0 ? '#f59e0b' : '#64748b', fontWeight: inc.days_lost > 0 ? 700 : 400 }}>
                        {inc.days_lost > 0 ? inc.days_lost : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {requiereAviso(inc.incident_type) ? (() => {
                          const av = estadoAvisoCaso(inc, hoy);
                          return <span style={{ color: COLOR_AVISO[av.estado], fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {av.estado === 'en_termino' ? textoVence(av.fechaLimite, hoy) : ETIQUETA_AVISO[av.estado]}
                          </span>;
                        })() : <span style={{ color: '#475569', fontSize: '0.9rem' }}>—</span>}
                      </td>
                      <td style={td}>
                        <span style={{ color: STATUS_COLORS[inc.status], fontWeight: 600, fontSize: '0.8rem' }}>
                          {INCIDENT_STATUS_LABELS[inc.status]}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {inc.attachment_url && (
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); openIncidentEvidence(inc); }}
                              title={inc.attachment_name ?? 'Ver archivo adjunto'}
                              style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.9rem', cursor: 'pointer' }}
                            >
                              📎
                            </button>
                          )}
                          {requiereAviso(inc.incident_type) && (
                            <button type="button" onClick={e => { e.stopPropagation(); setAvisoIncident(inc); }}
                              style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #1D6FA3', color: '#7dd3fc', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                              Aviso y medidas
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={e => { e.stopPropagation(); setModalIncident(inc); }}
                              style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              Editar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filtered.length} de {incidents.length} registro{incidents.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Modal */}
      {modalIncident !== undefined && (
        <IncidentModal
          companyId={companyId}
          incident={modalIncident}
          tipoInicial={tipoNuevo}
          workers={workers}
          warehouses={warehouses}
          onSaved={handleSaved}
          onClose={() => setModalIncident(undefined)}
        />
      )}

      {avisoIncident && (
        <IncidentAvisoMedidasModal
          companyId={companyId}
          companyName={displayCompany?.name ?? ''}
          incident={avisoIncident}
          workerName={avisoIncident.worker_id ? workerMap.get(avisoIncident.worker_id)?.full_name ?? null : null}
          warehouseName={avisoIncident.warehouse_id ? warehouseMap.get(avisoIncident.warehouse_id) ?? null : null}
          canEdit={canEdit}
          onChanged={loadData}
          onClose={() => setAvisoIncident(null)}
        />
      )}

      {investigationIncidentId !== undefined && (
        <IncidentInvestigationModal
          companyId={companyId}
          incidentId={investigationIncidentId}
          onSaved={() => setInvestigationIncidentId(undefined)}
          onClose={() => setInvestigationIncidentId(undefined)}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color, bg }: { label: string; value: number; color: string; bg: string }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</span>
      <div style={{ height: 3, borderRadius: 2, background: bg, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: color, opacity: 0.5 }} />
      </div>
      <span style={{ color: '#64748b', fontSize: '0.76rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function IndexCard({ label, value, subtitle, ref_ }: { label: string; value: string; subtitle: string; ref_: string }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 800 }}>{value}</span>
        <span style={{ color: '#475569', fontSize: '0.7rem' }}>{ref_}</span>
      </div>
      <span style={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600 }}>{label}</span>
      <span style={{ color: '#475569', fontSize: '0.72rem', lineHeight: 1.4 }}>{subtitle}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const secondaryBtn: React.CSSProperties = { padding: '9px 14px', background: 'transparent', border: '1px solid #475569', color: '#cbd5e1', fontWeight: 600, fontSize: '0.84rem', borderRadius: 8, cursor: 'pointer' };
const primaryBtn: React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const filterInp: React.CSSProperties = { padding: '8px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.84rem', outline: 'none' };
const td: React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
